// web/src/components/Filters.jsx
import PmcaAutocomplete from '@components/AutoComplete/PmcaAutocomplete';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import {
  setGlobalClassification,
  setGlobalCluster,
  setGlobalCompany,
  setGlobalCompanyEntity,
  setGlobalCounterparty,
  setGlobalCurrency,
  setGlobalInstrument,
  setGlobalSponsor,
  toggleFilterOpen,
} from '@store/global/globalSlice';
import {
  fetchAllTransactions,
  fetchClassifications,
  fetchClusters,
  fetchCompanies,
  fetchCompanyEntities,
  fetchCounterparties,
  fetchCurrencies,
  fetchInstruments,
  fetchSponsors,
  fetchTransactions,
  setClearAllFilters,
} from '@store/global/globalThunks';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Root } from './styles';

const Filters = () => {
  const dispatch = useDispatch();

  // Selectors to access filter data and selections from the Redux store
  const {
    sponsors,
    instruments,
    currencies,
    counterparties,
    companyEntities,
    companies,
    clusters,
    classifications,
    isFilterOpen,
    loading,
    error,
    globalCompany,
    globalCompanyEntity,
    globalSponsor,
    globalInstrument,
    globalCounterparty,
    globalClassification,
    globalCluster,
    globalCurrency,
  } = useSelector((state) => state.global);
  // Select authentication status from the Redux store
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  // Fetch all filter data on component mount **only if authenticated**
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchSponsors());
      dispatch(fetchInstruments());
      dispatch(fetchCurrencies());
      dispatch(fetchCounterparties());
      dispatch(fetchCompanyEntities(null)); // Initial fetch without company ID
      dispatch(fetchCompanies());
      dispatch(fetchClusters());
      dispatch(fetchClassifications());
      dispatch(fetchTransactions());
      dispatch(fetchAllTransactions(true)); // inc pending,review trades for revaluation tool
    }
  }, [dispatch, isAuthenticated]);

  // Fetch company entities whenever a company is selected **only if authenticated**
  useEffect(() => {
    if (isAuthenticated && globalCompany && globalCompany.value) {
      dispatch(fetchCompanyEntities(globalCompany.value));
      // Clear the selected company entity when company changes
      dispatch(setGlobalCompanyEntity(null));
    }
  }, [dispatch, globalCompany, isAuthenticated]);

  // Handler to update global filters in the Redux store
  const onChangeGlobalFilters = () => {
    if (
      !globalCompany &&
      !globalCompanyEntity &&
      !globalSponsor &&
      !globalInstrument &&
      !globalCounterparty &&
      !globalClassification &&
      !globalCluster &&
      !globalCurrency
    ) {
      alert('Please select at least one filter option');
      return;
    }

    // Toggle the filter panel
    dispatch(toggleFilterOpen());
  };

  // Handler to clear all filters
  const clearFilters = () => {
    dispatch(setClearAllFilters());
    dispatch(toggleFilterOpen());
  };

  // Memoized filter configuration for rendering
  const arrFilters = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Sponsor',
        value: globalSponsor,
        setValue: (value) => dispatch(setGlobalSponsor(value)),
        options: sponsors,
      },
      {
        id: 2,
        label: 'Company',
        value: globalCompany,
        setValue: (value) => dispatch(setGlobalCompany(value)),
        options: companies,
      },
      {
        id: 3,
        label: 'Company Entity',
        value: globalCompanyEntity,
        setValue: (value) => dispatch(setGlobalCompanyEntity(value)),
        options: companyEntities,
      },
      {
        id: 4,
        label: 'Instrument',
        value: globalInstrument,
        setValue: (value) => dispatch(setGlobalInstrument(value)),
        options: instruments,
      },
      {
        id: 5,
        label: 'Counterparty',
        value: globalCounterparty,
        setValue: (value) => dispatch(setGlobalCounterparty(value)),
        options: counterparties,
      },
      {
        id: 6,
        label: 'Classification',
        value: globalClassification,
        setValue: (value) => dispatch(setGlobalClassification(value)),
        options: classifications,
      },
      {
        id: 7,
        label: 'Cluster',
        value: globalCluster,
        setValue: (value) => dispatch(setGlobalCluster(value)),
        options: clusters,
      },
      {
        id: 8,
        label: 'Currency',
        value: globalCurrency,
        setValue: (value) => dispatch(setGlobalCurrency(value)),
        options: currencies,
      },
    ];
  }, [
    dispatch,
    sponsors,
    companies,
    companyEntities,
    instruments,
    counterparties,
    classifications,
    clusters,
    currencies,
    globalSponsor,
    globalCompany,
    globalCompanyEntity,
    globalInstrument,
    globalCounterparty,
    globalClassification,
    globalCluster,
    globalCurrency,
  ]);

  // Determine if any data is loading
  const isAnyLoading = loading;

  return (
    isFilterOpen &&
    isAuthenticated && ( // **Only render Filters if open and authenticated**
      <Root>
        {isAnyLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ flexWrap: 'wrap', width: '100%' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {arrFilters.map((filter) => (
                <Box mx={1} mb={1} key={filter.id} sx={{ minWidth: 200 }}>
                  <PmcaAutocomplete
                    label={filter.label}
                    value={filter.value}
                    onChange={filter.setValue}
                    options={filter.options}
                    enableSelectedTooltip
                  />
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={onChangeGlobalFilters}
                sx={{ mr: 1 }}>
                Confirm
              </Button>

              <Button
                variant="contained"
                color="secondary"
                onClick={clearFilters}>
                Clear Filters
              </Button>
            </Box>
          </Box>
        )}
      </Root>
    )
  );
};

export default Filters;
